import _ from 'lodash';
import { useState, useEffect } from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Grid,
    Checkbox,
    Tooltip,
    TextField,
    IconButton,
    InputAdornment,
    Typography,
    Button,
} from '@material-ui/core';
import { Search } from '@material-ui/icons';
import {
    faFileCsv,
} from '@fortawesome/pro-light-svg-icons';
import { ColumnFilter } from '@commonClient2/components';
import AuthService from '../../../services/auth';
import { UPDATE_MANAGER_APP_DATA } from '../../../Manager/store/appData';
import './ReportFilters.scss';

/**
 * @param {{ rank: number; employeeId: number; name: string; }[]} allEmployeeOptionsInFilter
 * @param {number[]} employees
 * @returns {EmployeeFilter[]}
 */
const prepareEmployeeList = (allEmployeeOptionsInFilter, employees) => (
    allEmployeeOptionsInFilter
        .sort((x, y) => x.rank - y.rank)
        .map((x) => ({
            employeeId: x.employeeId,
            name: x.name,
            selected: employees.includes(x.employeeId),
            rank: x.rank,
        }))
);

/**
 * @typedef {{ rank: number; employeeId: number; name: string; selected: boolean; }} EmployeeFilter
 * @param {{
 *  profileId: string,
 *  selectedEmployees: number[],
 *  allEmployeeOptionsInFilter: { rank: number; employeeId: number; name: string; }[],
 *  dataviewFields: ManagerAppData.IDataviewFilter[],
 *  downloadReportAsCsv: (profileId:string, employeeIds: number[], selectedDataviews: string[], searchString:string) => void,
 *  hasDataviews: boolean,
 *  handleApplyFilter: () => void,
 *  onChangeSelectedEmployees: Function,
 *  searchString: string,
 *  onChangeSearchString: Function,
 *  onChangeDataviewColumns: Function,
 * }} param0
*/

function ReportFilters({
    profileId, selectedEmployees, allEmployeeOptionsInFilter, dataviewFields, downloadReportAsCsv, hasDataviews, handleApplyFilter, onChangeSelectedEmployees, searchString, onChangeSearchString, onChangeDataviewColumns,
}) {
    const dispatch = useDispatch();
    const isManager = AuthService.isManager();

    const [employeeList, setEmployeeList] = useState(prepareEmployeeList(allEmployeeOptionsInFilter, selectedEmployees));

    const handleDownloadCsv = () => {
        const employeeIds = employeeList.filter((x) => x.selected).map((x) => x.employeeId);
        const selectedDataviews = dataviewFields.filter((x) => x.checked).map((x) => x.key);
        dispatch(downloadReportAsCsv(profileId, employeeIds, selectedDataviews, searchString));
    };

    /**
     * @param {string[]} selectedColumns
     */
    const handleDataviewFilterChange = (selectedColumns) => {
        const newColumnSelection = [...dataviewFields].map((x) => ({ ...x, checked: selectedColumns.includes(x.value) }));

        dispatch({
            type: UPDATE_MANAGER_APP_DATA,
            payload: { dataviewFields: newColumnSelection },
        });
        onChangeDataviewColumns(newColumnSelection.filter((x) => x.checked).map((x) => `${x.value}`));
    };

    useEffect(() => {
        setEmployeeList(prepareEmployeeList(allEmployeeOptionsInFilter, selectedEmployees));
    }, [JSON.stringify(selectedEmployees)]);

    /**
     * @param {EmployeeFilter[]} values
     */
    const setEmployeeSelected = (values) => {
        const updatedEmployees = employeeList.map((x) => ({ ...x, selected: values.includes(x) }));
        setEmployeeList(updatedEmployees);
        onChangeSelectedEmployees(values.map((x) => x.employeeId));
    };

    /**
     * @param {string} str
     */
    const setSearch = (str) => {
        onChangeSearchString(str);
    };

    return (
        <Grid
            container
            justifyContent="space-between"
            className="report-filter-grid-container"
        >
            <Grid
                item
                xs={12}
                lg={3}
                className="report-filter-grid-item"
            >
                <TextField
                    id="react-search-input"
                    size="small"
                    variant="outlined"
                    placeholder="Search"
                    value={searchString}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment
                                position="start"
                            >
                                <Search />
                            </InputAdornment>
                        ),
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            handleApplyFilter();
                        }
                    }}
                />
            </Grid>
            <Grid
                item
                xs={12}
                lg={3}
                className="report-filter-grid-item"
            >
                <Autocomplete
                    multiple
                    id="report-filter-multiple-select"
                    className="report-filter-multiple-select"
                    options={employeeList}
                    value={employeeList.filter((x) => x.selected)}
                    limitTags={1}
                    disableCloseOnSelect
                    getOptionLabel={(option) => option.name}
                    onChange={(event, values) => setEmployeeSelected(values)}
                    renderOption={(option) => (
                        <>
                            <Checkbox
                                style={{ marginRight: 8 }}
                                checked={option.selected}
                                color="primary"
                            />
                            {option.name}
                        </>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            variant="outlined"
                            label="Employees"
                        />
                    )}
                    renderTags={(value) => {
                        const numTags = value.length;
                        const limitTagsShown = 1;
                        return (
                            <Typography variant="body2">
                                <span className="report-filter-employee-names">
                                    {
                                        value
                                            .slice(0, limitTagsShown)
                                            .map((option) => option.name)
                                            .join(', ')
                                    }
                                </span>
                                <span>{numTags > limitTagsShown && ` +${numTags - limitTagsShown} more`}</span>
                            </Typography>
                        );
                    }}
                />
                <Button
                    onClick={(e) => handleApplyFilter()}
                    variant="contained"
                    className="report-filter-apply-btn"
                >
                    Apply filters
                </Button>
            </Grid>

            <Grid
                item
                xs={12}
                lg={6}
                className="report-filter-grid-item align-right"
            >
                {isManager && (
                    <div className="report-filter-button">
                        <Tooltip
                            title="Download to CSV"
                            arrow
                            placement="bottom-end"
                        >
                            <IconButton
                                className="report-filter-csv-button"
                                onClick={handleDownloadCsv}
                            >
                                <FontAwesomeIcon
                                    icon={faFileCsv}
                                />
                            </IconButton>
                        </Tooltip>
                    </div>
                )}

                {hasDataviews && (
                    <div className="report-filter-button">
                        <Tooltip
                            title="Column selection"
                            arrow
                        >
                            <ColumnFilter
                                options={dataviewFields}
                                onOptionSelected={handleDataviewFilterChange}
                            />
                        </Tooltip>
                    </div>
                )}
            </Grid>
        </Grid>
    );
}

export default ReportFilters;
